import { useMemo } from 'react';

import { RiDatabase2Line } from '@remixicon/react';

import {
  type ToolGroupEntry,
  ToolLabel,
  type ToolRenderOptions,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';

import { confidenceChatPlugin } from '../plugin';
import { ToolResponseDetails } from './ToolResponseDetails';
import { formatLineCount } from './formatLineCount';
import {
  formatToolResultContent,
  getInputString,
  getToolInput,
  parseToolResultEnvelopeObject,
  truncateToolSubject,
} from './parseToolResult';

type DataDiscoveryConfig = {
  running: string;
  completed: string;
  subjectKey?: string;
  subjectPrefix?: string;
  resultSummary?: 'tables' | 'schemaLines';
};

const dataDiscoveryTools: Record<string, DataDiscoveryConfig> = {
  mcp__data_explorer__search_tables: {
    running: 'Searching tables',
    completed: 'Searched tables',
    subjectKey: 'query',
    subjectPrefix: 'for',
    resultSummary: 'tables',
  },
  mcp__data_explorer__get_table_schema: {
    running: 'Reading table schema',
    completed: 'Read table schema',
    subjectKey: 'display_name',
    resultSummary: 'schemaLines',
  },
  mcp__data_explorer__get_warehouse_info: {
    running: 'Getting warehouse info',
    completed: 'Got warehouse info',
  },
};

const getResultSummary = (
  config: DataDiscoveryConfig,
  content: string,
): string | undefined => {
  if (!config.resultSummary) return undefined;
  const result = parseToolResultEnvelopeObject(content);
  if (config.resultSummary === 'tables' && Array.isArray(result?.tables)) {
    const tableCount = result.tables.length;
    return `${tableCount} ${tableCount === 1 ? 'table' : 'tables'}`;
  }
  if (
    config.resultSummary === 'schemaLines' &&
    typeof result?.ddl === 'string'
  ) {
    return formatLineCount(result.ddl);
  }
  return formatLineCount(formatToolResultContent(content));
};

export const DataDiscoveryToolLabel = ({
  entry,
  options,
}: {
  entry: ToolGroupEntry;
  options: ToolRenderOptions;
}) => {
  const config = dataDiscoveryTools[entry.tool];
  const input = getToolInput(entry.input);
  const subject =
    (config.subjectKey && getInputString(input, config.subjectKey)) ||
    (entry.tool === 'mcp__data_explorer__get_table_schema'
      ? getInputString(input, 'name')
      : undefined);
  const action =
    !options.isStreaming && entry.result?.success
      ? config.completed
      : config.running;
  const resultContent = entry.result?.content;
  const resultSummary = useMemo(
    () => (resultContent ? getResultSummary(config, resultContent) : undefined),
    [config, resultContent],
  );
  let summary: string | undefined;
  if (entry.result) {
    if (!entry.result.success) summary = 'Failed';
    else summary = resultSummary;
  }
  return (
    <ToolLabel.Root>
      <ToolLabel.Primary>
        {action}
        {!options.isStreaming && subject && (
          <>
            {' '}
            <span title={subject}>
              {config.subjectPrefix && `${config.subjectPrefix} `}
              {truncateToolSubject(subject)}
            </span>
          </>
        )}
      </ToolLabel.Primary>
      {summary && <ToolLabel.Secondary>{summary}</ToolLabel.Secondary>}
      <ToolLabel.Timer startedAt={entry.result ? undefined : entry.startedAt} />
      {entry.result && (
        <ToolLabel.Details>
          <ToolResponseDetails content={entry.result.content} />
        </ToolLabel.Details>
      )}
    </ToolLabel.Root>
  );
};

export const DataDiscoveryToolExtension = confidenceChatPlugin.provide(
  createToolExtension({
    id: 'data-explorer-discovery',
    predicate: tool => Boolean(dataDiscoveryTools[tool]),
    icon: RiDatabase2Line,
    display: 'grouped',
    collapseConsecutiveCalls: false,
    render: () => null,
    renderLabel: (entry, options) => (
      <DataDiscoveryToolLabel entry={entry} options={options} />
    ),
  }),
);
